import { useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGridPro, type GridColDef } from "@mui/x-data-grid-pro";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import InfoIcon from "@mui/icons-material/Info";
import RefreshIcon from "@mui/icons-material/Refresh";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  mapRowTypeToLabel,
  formatEtaDate,
  type StockTypeLabel,
} from "../helpers/stockAvailability";
import { StockDot } from "./StockDot";
import type { StockInventoryData, StockInventoryRow } from "./types";

export interface ProductStockInventoryTabProps {
  data: StockInventoryData | null;
  isLoading: boolean;
  isError: boolean;
  onRefresh: () => void;
}

interface StockGroupHeader {
  id: string;
  _isHeader: true;
  label: StockTypeLabel;
  location: string;
  inStock: number;
  onOrder: number;
}

// Plain data shape so grouped/sorted rows built via object spread don't need to satisfy a
// class's init/toJSON instance methods.
interface StockRowData {
  rowType?: number | string | null;
  location?: string | null;
  inStock?: number | null;
  onOrder?: number | null;
  onOrderEta?: Date | string | null;
}

type StockDataRow = StockRowData & {
  id: string;
  _isHeader?: false;
};

type AugmentedStockRow = StockDataRow | StockGroupHeader;

// Row groups are ordered manually (Owned -> Reserved -> SHI Stock) and rows within a group are
// pre-sorted by inStock desc, so DataGrid column sorting stays disabled to avoid breaking that
// structure. The "In Stock" header still shows a static sort-direction indicator to match the
// approved design.
const STOCK_GROUP_ORDER: StockTypeLabel[] = ["Owned", "Reserved", "SHI Stock"];

const STOCK_TYPE_LABELS: Record<StockTypeLabel, string> = {
  Owned: "OWNED STOCK",
  Reserved: "RESERVED STOCK",
  "SHI Stock": "SHI STOCK",
};

const STOCK_TYPE_TOOLTIPS: Record<StockTypeLabel, string> = {
  Owned:
    "Inventory that is typically paid for, owned, and allocated for use by your organization.",
  Reserved:
    "Inventory that is pre-allocated by SHI for your organization but is typically not yet paid for.",
  "SHI Stock":
    "General inventory available to all SHI customers and is not allocated for your organization. Availability of this stock will constantly change.",
};

// Mirrors the CS Standards drawer's "at risk" aggregate threshold.
const AT_RISK_THRESHOLD = 50;

const LAST_UPDATED_DATE_FORMATTER = new Intl.DateTimeFormat("en-US", {
  month: "long",
  day: "numeric",
  year: "numeric",
});

const LAST_UPDATED_TIME_FORMATTER = new Intl.DateTimeFormat("en-US", {
  hour: "numeric",
  minute: "2-digit",
  timeZoneName: "short",
});

function formatLastUpdated(date: Date): string {
  return `Last updated on ${LAST_UPDATED_DATE_FORMATTER.format(date)} at ${LAST_UPDATED_TIME_FORMATTER.format(date)}`;
}

function buildGroupedStockRows(
  rows: StockInventoryRow[],
): AugmentedStockRow[] {
  const normalizedRows = rows.map((row) => ({
    ...row,
    stockLabel: mapRowTypeToLabel(row.rowType),
  }));
  const result: AugmentedStockRow[] = [];

  for (const label of STOCK_GROUP_ORDER) {
    const group = normalizedRows.filter((r) => r.stockLabel === label);
    if (group.length === 0) continue;

    const sumInStock = group.reduce((s, r) => s + (r.inStock ?? 0), 0);
    const sumOnOrder = group.reduce((s, r) => s + (r.onOrder ?? 0), 0);

    result.push({
      id: `hdr-${label}`,
      _isHeader: true,
      label,
      location: label,
      inStock: sumInStock,
      onOrder: sumOnOrder,
    });

    group
      .sort((a, b) => (b.inStock ?? 0) - (a.inStock ?? 0))
      .forEach((row, index) =>
        result.push({ ...row, id: `${label}-${index}`, _isHeader: false }),
      );
  }

  return result;
}

const stockColumns: GridColDef<AugmentedStockRow>[] = [
  {
    field: "location",
    headerName: "Location",
    flex: 1,
    minWidth: 140,
    sortable: false,
    disableColumnMenu: true,
    renderCell: ({ row }) => {
      if (row._isHeader) {
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              height: "100%",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                color: "text.primary",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
              }}
            >
              {STOCK_TYPE_LABELS[row.label]}
            </Typography>
            <Tooltip
              title={STOCK_TYPE_TOOLTIPS[row.label]}
              placement="top"
              arrow
            >
              <Box
                component="span"
                sx={{
                  display: "inline-flex",
                  position: "relative",
                  zIndex: 10,
                  pointerEvents: "auto",
                }}
              >
                <InfoIcon
                  sx={{ fontSize: 16, color: "text.disabled", cursor: "help" }}
                />
              </Box>
            </Tooltip>
          </Box>
        );
      }
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, height: "100%" }}>
          <StockDot
            status={(row.inStock ?? 0) > 0 ? "in-stock" : "out-of-stock"}
            label=""
            showLabel={false}
            size={7}
          />
          <Typography variant="body2">{row.location ?? ""}</Typography>
        </Box>
      );
    },
  },
  {
    field: "onOrderEta",
    headerName: "On Order ETA",
    width: 130,
    align: "right",
    headerAlign: "right",
    sortable: false,
    disableColumnMenu: true,
    renderCell: ({ row }) => {
      if (row._isHeader) return null;
      const eta = formatEtaDate(row.onOrderEta);
      return (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", height: "100%", width: "100%" }}>
          <Typography variant="body2" color="text.secondary">
            {eta}
          </Typography>
        </Box>
      );
    },
  },
  {
    field: "onOrder",
    headerName: "On Order",
    width: 100,
    align: "right",
    headerAlign: "right",
    sortable: false,
    disableColumnMenu: true,
    renderCell: ({ row }) => {
      const qty = row.onOrder ?? 0;
      return (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", height: "100%", width: "100%" }}>
          <Typography variant="body2" fontWeight={row._isHeader ? 600 : 400} color={qty > 0 ? "text.primary" : "text.disabled"}>
            {qty > 0 ? String(qty) : ""}
          </Typography>
        </Box>
      );
    },
  },
  {
    field: "inStock",
    headerName: "In Stock",
    width: 110,
    align: "right",
    headerAlign: "right",
    sortable: false,
    disableColumnMenu: true,
    renderHeader: () => (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.5, width: "100%" }}>
        <ArrowDownwardIcon sx={{ fontSize: 14, color: "text.secondary" }} />
        <Typography variant="caption" sx={{ fontWeight: 600, color: "text.primary", fontSize: 13 }}>
          In Stock
        </Typography>
      </Box>
    ),
    renderCell: ({ row }) => {
      if (row._isHeader) {
        const atRisk = row.inStock < AT_RISK_THRESHOLD;
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              width: "100%",
              height: "100%",
              gap: 0.5,
            }}
          >
            {atRisk && (
              <Tooltip title="Inventory Low / At Risk" placement="top">
                <Box
                  component="span"
                  sx={{
                    display: "inline-flex",
                    position: "relative",
                    zIndex: 10,
                    pointerEvents: "auto",
                  }}
                >
                  <WarningAmberIcon
                    sx={{ fontSize: 16, color: "warning.main" }}
                  />
                </Box>
              </Tooltip>
            )}
            <Typography variant="body2" fontWeight={600}>
              {row.inStock}
            </Typography>
          </Box>
        );
      }
      return (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", width: "100%", height: "100%" }}>
          <Typography variant="body2">{row.inStock ?? 0}</Typography>
        </Box>
      );
    },
  },
];

export function ProductStockInventoryTab({
  data,
  isLoading,
  isError,
  onRefresh,
}: ProductStockInventoryTabProps) {
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date | null>(null);

  useEffect(() => {
    if (data) setLastUpdatedAt(new Date());
  }, [data]);

  if (isLoading) {
    return (
      <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress size={32} aria-label="Loading stock and inventory" />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", p: 4, textAlign: "center" }}>
        <Typography variant="body2" color="error">
          Failed to load stock and inventory details.
        </Typography>
      </Box>
    );
  }

  const rows = buildGroupedStockRows(data?.productAvailability ?? []);

  if (rows.length === 0) {
    return (
      <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", p: 4, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          No stock and inventory data available.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
      <DataGridPro
        rows={rows}
        columns={stockColumns}
        getRowId={(row) => String(row.id)}
        disableColumnMenu
        disableRowSelectionOnClick
        hideFooter
        columnHeaderHeight={40}
        getRowHeight={(params) => (params.model._isHeader ? 46 : 38)}
        getRowClassName={(params) => (params.row._isHeader ? "stock-group-header" : "")}
        sx={{
          flex: 1,
          minHeight: 0,
          border: "none",
          bgcolor: "background.paper",
          "& .MuiDataGrid-columnHeaders": { bgcolor: "background.default" },
          "& .MuiDataGrid-columnHeaderRow": { bgcolor: "background.default" },
          "& .MuiDataGrid-columnSeparator": { display: "none" },
          "& .MuiDataGrid-cell": { outline: "none !important" },
          "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": { outline: "none" },
          "& .MuiDataGrid-row:hover": { bgcolor: "action.hover" },
          "& .stock-group-header": { bgcolor: "background.default" },
          "& .stock-group-header:hover": { bgcolor: "background.default" },
          "& .MuiDataGrid-cell[data-colindex='0'], & .MuiDataGrid-columnHeader[data-colindex='0']": { pl: 2.5 },
          "& .MuiDataGrid-cell[data-colindex='3'], & .MuiDataGrid-columnHeader[data-colindex='3']": { pr: 2.5 },
        }}
      />
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
          px: 2.5,
          py: 1,
          borderTop: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic" }}>
          {lastUpdatedAt ? formatLastUpdated(lastUpdatedAt) : ""}
        </Typography>
        <Tooltip title="Refresh stock data" placement="left">
          <IconButton size="small" aria-label="Refresh stock data" onClick={onRefresh} sx={{ color: "text.secondary" }}>
            <RefreshIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}
